import { useCallback, useMemo } from "react";
import { Home, Percent, Store } from "lucide-react";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { PageFrame, StatCard, StatCardGrid } from "@/components/common";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { salesAgentService } from "@/services/salesAgentService";
import { formatCommissionPercentLabel } from "@/lib/commissionRate";
import { ClickToCopyId } from "@/components/common/ClickToCopyId";
import {
  DataTable,
  DataTablePrimaryText,
  type DataTableColumn,
} from "@/components/table";
import type { SalesAgentMeAssociationItem } from "@/types";

type SortColumn = "name" | "owner" | "rate";

export default function SalesAgentAssociationsPage() {
  const { t } = useTranslation("salesAgent");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["sales-agent-associations"],
    queryFn: () => salesAgentService.getMyAssociations(),
  });

  const scopeLabel = useCallback(
    (scope: SalesAgentMeAssociationItem["scope"]) =>
      scope === "activity"
        ? t("associations.scopeActivity")
        : scope === "house"
          ? t("associations.scopeHouse")
          : scope,
    [t],
  );

  const rows = data ?? [];
  const activityCount = rows.filter((row) => row.scope === "activity").length;
  const houseCount = rows.filter((row) => row.scope === "house").length;
  const topRate = rows.reduce(
    (max, row) => Math.max(max, row.commission_rate),
    0,
  );

  const columns = useMemo<
    DataTableColumn<SalesAgentMeAssociationItem, SortColumn>[]
  >(
    () => [
      {
        id: "name",
        header: t("associations.colName"),
        sortKey: "name",
        sortKind: "text",
        getSortValue: (row) => row.name,
        defaultWidth: 280,
        alwaysVisible: true,
        cell: (row) => {
          const isHouse = row.scope === "house";
          const ScopeIcon = isHouse ? Home : Store;
          return (
            <span className="inline-flex min-w-0 items-center gap-3">
              <span
                className={cn(
                  "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset",
                  isHouse
                    ? "bg-success-50 text-success-600 ring-success-600/15"
                    : "bg-primary-50 text-primary-600 ring-primary-600/15",
                )}
              >
                <ScopeIcon
                  className="h-4 w-4"
                  aria-hidden
                />
              </span>
              <span className="min-w-0">
                <DataTablePrimaryText className="truncate">
                  {row.name}
                </DataTablePrimaryText>
                <span className="text-surface-500 flex min-w-0 items-center gap-1.5 text-xs">
                  {scopeLabel(row.scope)}
                  <span aria-hidden>·</span>
                  <ClickToCopyId
                    value={row.scope_id}
                    className="text-surface-500"
                  />
                </span>
              </span>
            </span>
          );
        },
      },
      {
        id: "owner",
        header: t("associations.colOwner"),
        sortKey: "owner",
        sortKind: "text",
        getSortValue: (row) => row.seller_name,
        defaultWidth: 180,
        cell: (row) => (
          <span className="block min-w-0">
            <DataTablePrimaryText className="truncate">
              {row.seller_name}
            </DataTablePrimaryText>
            <ClickToCopyId
              value={row.seller_user_id}
              className="text-surface-500 text-xs"
            />
          </span>
        ),
      },
      {
        id: "rate",
        header: t("associations.colRate"),
        align: "right",
        sortKey: "rate",
        sortKind: "number",
        getSortValue: (row) => row.commission_rate,
        defaultWidth: 100,
        cell: (row) => (
          <span className="bg-accent-50 text-accent-700 ring-accent-600/20 inline-flex items-center rounded-md px-2 py-0.5 text-[13px] font-semibold tabular-nums ring-1 ring-inset">
            {formatCommissionPercentLabel(row.commission_rate)}
          </span>
        ),
      },
    ],
    [scopeLabel, t],
  );

  return (
    <PageFrame
      title={t("associations.title")}
      description={t("associations.description")}
      isLoading={isLoading}
      loadingMessage={t("associations.loading")}
      error={!!error || !data}
      errorMessage={t("associations.errorLoad")}
      onRetry={() => void refetch()}
    >
      {rows.length > 0 ? (
        <StatCardGrid layout="1-2-3">
          <StatCard
            title={t("associations.statActivities")}
            value={activityCount}
            icon={Store}
            iconColor="primary"
          />
          <StatCard
            title={t("associations.statHouses")}
            value={houseCount}
            icon={Home}
            iconColor="success"
          />
          <StatCard
            title={t("associations.statTopRate")}
            value={formatCommissionPercentLabel(topRate)}
            icon={Percent}
            iconColor="accent"
          />
        </StatCardGrid>
      ) : null}

      <DataTable
        labels={tableLabels}
        closeLabel={tCommon("close")}
        loadingLabel={tCommon("loading")}
        rows={rows}
        columns={columns}
        getRowKey={(row) => `${row.scope}-${row.scope_id}`}
        defaultSortColumn="name"
        preferencesKey="sales-agent-associations"
        emptyMessage={t("associations.empty")}
      />
    </PageFrame>
  );
}
